// Annual rent indexation (ICC / ILC / ILAT). Pure calculation, shared by the web app,
// quittance creation and the MCP server.
import { allocateVatByRate } from './lease-lines.ts';

export const INDEX_CODES = ['ICC', 'ILC', 'ILAT'] as const;

// Lease columns needed by quittance creation to apply the revision.
export const INDEXATION_LEASE_COLUMNS =
  'billing_frequency, deposit_amount, monthly_rent, deposit_months, indexation_enabled, indexation_index, indexation_quarter, indexation_invoice_month, indexation_effective_month, indexation_base_year, indexation_base_value';

export interface IndexationInput {
  year: number;            // revision year N
  indexCode: string;       // 'ICC' | 'ILC' | 'ILAT'
  indexQuarter: number;    // reference quarter (e.g. 2)
  baseYear: number;        // year of the base index (N-1)
  baseValue: number;       // base index (e.g. 2086)
  newValue: number;        // new index (e.g. 2103)
  oldMonthlyRent: number;  // monthly rent HT before revision
  currentDeposit: number;  // deposit held before revision
  depositMonths: number;   // e.g. 3
  vatRate: number;         // e.g. 20
  chargesHt: number;       // charges HT billed in this period (unchanged by indexation)
  invoiceMonth: number;    // 10 = October (period carrying the revision)
  effectiveMonth: number;  // 11 = November (first month at the new rent)
  monthsInCall: number;    // 3 for quarterly billing, 1 for monthly
}

// Same shape as InvoiceTemplateData.lineItems, stored in rent_periods.invoice_lines.
export interface InvoiceLine {
  label: string;
  annotation?: string | null;
  type: 'rent' | 'charges' | 'deposit';
  amountHT: number;
  vatRate: number;
  amountTTC: number;
}

export interface IndexationResult {
  newMonthlyRent: number;
  monthlyIncrease: number;
  variationPct: number;
  newPeriodRent: number;
  newDeposit: number;
  depositAdjustment: number;
  lines: InvoiceLine[];
  totals: { rentHt: number; rentTtc: number; chargesHt: number; chargesTtc: number; vat: number; deposit: number; toPay: number };
}

const MONTHS = ['janvier', 'février', 'mars', 'avril', 'mai', 'juin', 'juillet', 'août',
  'septembre', 'octobre', 'novembre', 'décembre'];

export const round2 = (x: number) => Math.round((x + Number.EPSILON) * 100) / 100;

const eur = (x: number) =>
  x.toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).replace(/\u202f|\u00a0/g, ' ') + ' €';
const idx = (x: number) => x.toLocaleString('fr-FR').replace(/\u202f|\u00a0/g, ' ');
const quarterLabel = (q: number) => (q === 1 ? '1er' : `${q}e`) + ' trimestre';
const esc = (v: unknown) => String(v ?? '').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');

function line(type: InvoiceLine['type'], label: string, annotation: string | null, ht: number, vat: number): InvoiceLine {
  const amountHT = round2(ht);
  return { type, label, annotation, amountHT, vatRate: vat, amountTTC: round2(amountHT * (1 + vat / 100)) };
}

// Is month m of the billed period already at the new rent? (handles the year boundary)
function isAfterEffective(m: number, invoiceMonth: number, effectiveMonth: number): boolean {
  const offM = (m - invoiceMonth + 12) % 12;
  const offE = (effectiveMonth - invoiceMonth + 12) % 12;
  return offM >= offE;
}
const effYear = (i: IndexationInput) => i.year + (i.effectiveMonth < i.invoiceMonth ? 1 : 0);

export function computeIndexation(i: IndexationInput): IndexationResult {
  if (!(i.baseValue > 0) || !(i.newValue > 0)) throw new Error('Indice manquant ou invalide');

  // Monthly rent is rounded to the cent first, then multiplied.
  const newMonthlyRent = round2(i.oldMonthlyRent * i.newValue / i.baseValue);
  const newDeposit = round2(newMonthlyRent * i.depositMonths);
  const depositAdjustment = round2(newDeposit - i.currentDeposit);

  const lines: InvoiceLine[] = [];
  for (let k = 0; k < i.monthsInCall; k++) {
    const m = ((i.invoiceMonth - 1 + k) % 12) + 1;
    const y = i.year + Math.floor((i.invoiceMonth - 1 + k) / 12);
    const label = `Loyer principal — ${MONTHS[m - 1]} ${y}`;
    if (isAfterEffective(m, i.invoiceMonth, i.effectiveMonth)) {
      lines.push(line('rent', label, `Nouveau loyer indexé ${i.indexCode}`, newMonthlyRent, i.vatRate));
    } else {
      lines.push(line('rent', label,
        `Ancien loyer. Augmentation du loyer à compter du 1er ${MONTHS[i.effectiveMonth - 1]} ${effYear(i)} (indexation ${i.indexCode}, voir annexe).`,
        i.oldMonthlyRent, i.vatRate));
    }
  }
  if (i.chargesHt > 0) lines.push(line('charges', 'Charges locatives', null, i.chargesHt, i.vatRate));
  if (depositAdjustment !== 0) {
    lines.push(line('deposit', 'Ajustement du dépôt de garantie',
      `${i.depositMonths} × ${eur(newMonthlyRent)} = ${eur(newDeposit)} − dépôt détenu ${eur(i.currentDeposit)} (non soumis à TVA)`,
      depositAdjustment, 0));
  }
  const invoiceLines = allocateVatByRate(lines);

  const sum = (type: InvoiceLine['type'], k: 'amountHT' | 'amountTTC') =>
    round2(invoiceLines.filter((l) => l.type === type).reduce((s, l) => s + l[k], 0));
  const rentHt = sum('rent', 'amountHT');
  const rentTtc = sum('rent', 'amountTTC');
  const chargesHt = sum('charges', 'amountHT');
  const chargesTtc = sum('charges', 'amountTTC');

  return {
    newMonthlyRent,
    monthlyIncrease: round2(newMonthlyRent - i.oldMonthlyRent),
    variationPct: round2((i.newValue / i.baseValue - 1) * 100),
    newPeriodRent: round2(newMonthlyRent * i.monthsInCall),
    newDeposit,
    depositAdjustment,
    lines: invoiceLines,
    totals: {
      rentHt, rentTtc, chargesHt, chargesTtc,
      vat: round2(rentTtc - rentHt + chargesTtc - chargesHt),
      deposit: depositAdjustment,
      toPay: round2(rentTtc + chargesTtc + depositAdjustment),
    },
  };
}

// Lease → calculation input for the given period (YYYY-MM).
// deno-lint-ignore no-explicit-any
export function indexationInputForLease(lease: any, period: string, newValue: number): IndexationInput {
  const [year] = period.split('-').map(Number);
  const monthsInCall = lease.billing_frequency === 'quarterly' ? 3 : 1;
  return {
    year,
    indexCode: lease.indexation_index || 'ICC',
    indexQuarter: Number(lease.indexation_quarter) || 2,
    baseYear: Number(lease.indexation_base_year),
    baseValue: Number(lease.indexation_base_value),
    newValue,
    oldMonthlyRent: Number(lease.monthly_rent) || round2((Number(lease.rent_amount) || 0) / monthsInCall),
    currentDeposit: Number(lease.deposit_amount) || 0,
    depositMonths: Number(lease.deposit_months ?? 3),
    vatRate: Number(lease.vat_rate) || 0,
    chargesHt: Number(lease.charges_amount) || 0,
    invoiceMonth: Number(lease.indexation_invoice_month) || 10,
    effectiveMonth: Number(lease.indexation_effective_month) || 11,
    monthsInCall,
  };
}

// deno-lint-ignore no-explicit-any
export function isIndexationDue(lease: any, period: string): boolean {
  if (!lease?.indexation_enabled) return false;
  const [year, month] = period.split('-').map(Number);
  return month === (Number(lease.indexation_invoice_month) || 10)
    && Number(lease.indexation_base_year || 0) < year
    && Number(lease.indexation_base_value) > 0;
}

// Annex table attached to the quittance email.
export function renderAnnexHtml(p: {
  sciName: string; tenantName: string; propertyLabel: string;
  input: IndexationInput; result: IndexationResult;
}): string {
  const { input: i, result: r } = p;
  const effDate = `01/${String(i.effectiveMonth).padStart(2, '0')}/${effYear(i)}`;
  const nextEff = `01/${String(i.effectiveMonth).padStart(2, '0')}/${effYear(i) + 1}`;
  const q = quarterLabel(i.indexQuarter);
  const row = (a: string, b: string) => `<tr><td>${a}</td><td class="n">${b}</td></tr>`;
  const sub = (a: string) => `<tr class="sub"><td colspan="2">${a}</td></tr>`;
  const plus = (x: number) => (x >= 0 ? '+ ' : '− ') + eur(Math.abs(x));
  const rentLines = r.lines.filter((l) => l.type === 'rent');
  const periodWord = i.monthsInCall === 3 ? 'trimestriel' : 'mensuel';

  return `<!DOCTYPE html><html lang="fr"><head><meta charset="utf-8">
<title>Annexe — Révision annuelle du loyer ${i.year}</title></head><body style="margin:40px">
<section class="ix-annex">
<style>
.ix-annex{font-family:Arial,Helvetica,sans-serif;color:#1c2430;font-size:13px}
.ix-annex h1{font-size:20px;color:#1f4e79;margin:0 0 4px}
.ix-annex .muted{color:#5b6675}
.ix-annex .formula{font-size:16px;margin:18px 0;padding:14px;border:1px solid #d5dae1;text-align:center}
.ix-annex .formula b{color:#1f4e79}
.ix-annex table{width:100%;border-collapse:collapse}
.ix-annex th{text-align:left;font-size:12px;color:#5b6675;border-bottom:1px solid #1c2430;padding:7px 10px}
.ix-annex td{padding:8px 10px;border-bottom:1px solid #d5dae1}
.ix-annex td.n,.ix-annex th.n{text-align:right;white-space:nowrap}
.ix-annex tr.sub td{background:#f6f8fa;font-weight:bold}
.ix-annex .note{background:#eaf1f8;border-left:3px solid #1f4e79;padding:10px 12px;margin-top:18px}
.ix-annex .foot{margin-top:24px;font-size:11px;color:#5b6675;text-align:center}
</style>
<h1>Annexe — Révision annuelle du loyer</h1>
<p class="muted">Bail ${esc(p.sciName)} / ${esc(p.tenantName)} — ${esc(p.propertyLabel)} — indexation au ${effDate}</p>
<div class="formula">Nouveau loyer = <b>${eur(i.oldMonthlyRent)}</b> × <b>${idx(i.newValue)}</b> ÷ <b>${idx(i.baseValue)}</b> = <b>${eur(r.newMonthlyRent)} HT / mois</b></div>
<table><thead><tr><th>Élément</th><th class="n">Valeur</th></tr></thead><tbody>
${sub(`Indices de référence (${esc(i.indexCode)} – INSEE)`)}
${row(`Indice de base : ${esc(i.indexCode)} ${q} ${i.baseYear}`, idx(i.baseValue))}
${row(`Nouvel indice : ${esc(i.indexCode)} ${q} ${i.year}`, idx(i.newValue))}
${row('Variation annuelle', (r.variationPct >= 0 ? '+ ' : '− ') + Math.abs(r.variationPct).toLocaleString('fr-FR') + ' %')}
${sub('Loyer principal hors taxes')}
${row('Loyer mensuel avant révision', eur(i.oldMonthlyRent))}
${row('Loyer mensuel après révision', eur(r.newMonthlyRent))}
${row('Augmentation mensuelle', plus(r.monthlyIncrease))}
${i.monthsInCall > 1 ? `${row(`Loyer ${periodWord} avant révision`, eur(round2(i.oldMonthlyRent * i.monthsInCall)))}
${row(`Loyer ${periodWord} après révision`, eur(r.newPeriodRent))}
${row(`Augmentation ${periodWord}`, plus(round2(r.newPeriodRent - i.oldMonthlyRent * i.monthsInCall)))}` : ''}
${row(`Loyer ${periodWord} après révision TTC (TVA ${i.vatRate} %)`, eur(round2(r.newPeriodRent * (1 + i.vatRate / 100))))}
${sub(`Dépôt de garantie (${i.depositMonths} mois de loyer principal HT)`)}
${row('Dépôt avant révision', eur(i.currentDeposit))}
${row(`Dépôt après révision (${i.depositMonths} × ${eur(r.newMonthlyRent)})`, eur(r.newDeposit))}
${row('Complément de dépôt à verser (non soumis à TVA)', eur(r.depositAdjustment))}
${sub('Application à la quittance (HT)')}
${rentLines.map((l) => row(l.label.replace('Loyer principal — ', '') + (l.annotation?.startsWith('Ancien') ? ' — ancien loyer' : ' — nouveau loyer'), eur(l.amountHT))).join('\n')}
${row('Total loyers de la période', eur(r.totals.rentHt))}
</tbody></table>
<div class="note">Prochaine révision au ${nextEff} sur la base de l'${esc(i.indexCode)} du ${q} ${i.year + 1}, l'${esc(i.indexCode)} du ${q} ${i.year} (${idx(i.newValue)}) devenant l'indice de base.</div>
<div class="foot">Source des indices : INSEE.</div>
</section>
</body></html>`;
}

const INDEX_NAMES: Record<string, string> = {
  ICC: 'indice du coût de la construction',
  ILC: 'indice des loyers commerciaux',
  ILAT: 'indice des loyers des activités tertiaires',
};

// Note printed on the quittance of the revision period, from a lease_indexations row.
// deno-lint-ignore no-explicit-any
export function indexationNote(rec: any, lease: any): string {
  if (!rec) return '';
  const code = String(rec.index_code);
  const q = Number(rec.index_quarter);
  const eff = Number(lease?.indexation_effective_month) || 11;
  const inv = Number(lease?.indexation_invoice_month) || 10;
  const effY = Number(rec.year) + (eff < inv ? 1 : 0);
  return `Révision annuelle du loyer selon l'${INDEX_NAMES[code] ?? code} (${esc(code)} – INSEE) : loyer mensuel porté de ${eur(Number(rec.old_monthly_rent))} HT à ${eur(Number(rec.new_monthly_rent))} HT (${esc(code)} T${q} ${rec.base_year} : ${idx(Number(rec.base_value))} → ${esc(code)} T${q} ${rec.year} : ${idx(Number(rec.new_value))}). Applicable à compter du 1er ${MONTHS[eff - 1]} ${effY}. Détail du calcul en annexe.`;
}
